import { FC, useState } from 'react';
import { App, Button, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DATE_FORMAT, TIME_FORMAT } from 'constants/dates';
import { DownloadOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useUIDispatchContext } from 'providers/UI';
import { useLazyQuery } from '@apollo/client';
import { AppointmentById } from 'gql/appointments/__generated__/AppointmentById';
import { GET_APPOINTMENT_ITEM } from 'gql/appointments/queries';
import { REPORT_STATUSES } from 'constants/reports';
import { Link } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import { setViewerPdf } from 'store/file/slice';

import {
  StyledReportCard,
  StyledReportCardContent,
  StyledTitle
} from './styled';

const { Text } = Typography;

interface IProps {
  id: string;
  appointmentId?: string | null;
  dateReport?: string | null;
  report?: string | null;
  status?: string | null;
}

const ReportCard: FC<IProps> = ({
  id,
  appointmentId,
  dateReport,
  report,
  status
}) => {
  const { message } = App.useApp();
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { toggleModal } = useUIDispatchContext();

  const [getAppointment] = useLazyQuery<AppointmentById>(GET_APPOINTMENT_ITEM, {
    fetchPolicy: 'no-cache',
    onError(err) {
      message.error(err?.message);
    }
  });

  const onClickViewReport = async () => {
    try {
      setLoading(true);
      const res = await getAppointment({
        variables: {
          id: appointmentId
        }
      });

      const report = res.data?.appointment?.data?.report;

      if (report) {
        toggleModal('reportPreview', true, {
          date: report.date_report ? dayjs(report.date_report).format() : '',
          aboutDoctor: report.doctor_info || '',
          report: report.report || '',
          patientBirth: report.date_of_birth
            ? dayjs(report.date_of_birth).format()
            : '',
          patientName: report.first_name || '',
          patientLastName: report.last_name || '',
          appointmentId: report.appointment_id || '',
          signature: report.signature || '',
          lang: report.lang,
          icd: report.icd
        });
      }
    } catch (err) {
      //
    } finally {
      setLoading(false);
    }
  };

  const onDownloadReport = () => {
    dispatch(setViewerPdf({ id, url: `appointments-reports/pdf/${id}` }));
  };

  return (
    <>
      <StyledReportCard>
        <StyledTitle>
          <Space>
            <Text style={{ fontWeight: 'bold' }}>#{id}</Text>
            {status === REPORT_STATUSES.sent ? (
              <Tag color="#48CA8C">{t('reports.report_sent')}</Tag>
            ) : (
              <Tag color="orange">{t('reports.draft')}</Tag>
            )}
          </Space>
          <Text style={{ fontWeight: 500, color: '#6B7083' }}>
            {dayjs(dateReport).format(`${DATE_FORMAT} ${TIME_FORMAT}`)}
          </Text>
        </StyledTitle>
        <StyledReportCardContent direction="vertical" size={12}>
          <Text style={{ fontWeight: 'bold' }}>{t('reports.report')}</Text>
          {!!report && (
            <div
              className="report-text"
              dangerouslySetInnerHTML={{
                __html: report || ''
              }}
            />
          )}
          {status === REPORT_STATUSES.draft ? (
            <Space size={16}>
              <Link to={`/report/details/${id}?id=${appointmentId}`}>
                <Button
                  style={{ minWidth: 130 }}
                  icon={<EditOutlined />}
                  type="primary"
                  ghost
                >
                  {t('reports.edit_report')}
                </Button>
              </Link>
            </Space>
          ) : (
            <Space size={16}>
              <Link
                to={`/pdf-viewer/${id}`}
                target="_blank"
                rel="noreferrer"
                onClick={onDownloadReport}
              >
                <Button icon={<DownloadOutlined />} type="primary" ghost>
                  {t('reports.download')}
                </Button>
              </Link>
              <Button
                icon={<EyeOutlined />}
                loading={isLoading}
                onClick={onClickViewReport}
              >
                {t('reports.view_report')}
              </Button>
            </Space>
          )}
        </StyledReportCardContent>
      </StyledReportCard>
    </>
  );
};

export default ReportCard;
